import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, MessageSquare } from "lucide-react";
import MaterialCarousel from "./MaterialCarousel";
import { fetchLibraryMaterials } from "../lib/library";

/* ---- Token parser ------------------------------------------------------
   Detects a `[MATERIAL_PICKER: Category]` directive emitted by the LLM in
   its reply. Returns { text, picker } — text is the original message with
   the directive stripped, picker is the category name (or null). The bot
   prompt enforces at most one directive per reply so we only return the
   first match.
*/
const PICKER_RE = /\[\s*MATERIAL_PICKER\s*:\s*([^\]]+?)\s*\]/i;

export const parseMaterialPicker = (raw) => {
  if (!raw || typeof raw !== "string") return { text: raw || "", picker: null };
  const m = raw.match(PICKER_RE);
  if (!m) return { text: raw, picker: null };
  const text = raw.replace(PICKER_RE, "").replace(/\n{3,}/g, "\n\n").trim();
  return { text, picker: m[1].trim() };
};

/* ---- Component ---------------------------------------------------------
   Horizontal-scrolling carousel of MaterialCarousel cards. Each card shows
   the gallery photos + "feels like" + "best for" copy. Tapping "Pick this"
   sends the material's label back into the chat as the customer's next
   message — which is what the LLM is waiting for.
*/
const InlineMaterialPicker = ({ category, onPick, onOpenFull }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setErr(null);
    fetchLibraryMaterials(category)
      .then((d) => { if (active) setData(d); })
      .catch((e) => { if (active) setErr(e?.response?.data?.detail || "Could not load materials"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [category]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  if (loading) {
    return (
      <div
        className="card-swiss p-3 rounded-sm flex items-center gap-2 text-sm text-stone-500"
        data-testid="inline-material-picker-loading"
      >
        <Loader2 size={14} className="animate-spin" /> Loading {category} options…
      </div>
    );
  }

  if (err || !data || !data.items || data.items.length === 0) {
    return (
      <div className="card-swiss p-3 rounded-sm text-xs text-stone-500" data-testid="inline-material-picker-empty">
        {err || `No catalog entries yet for ${category}.`}
      </div>
    );
  }

  return (
    <div
      className="card-swiss rounded-sm p-3 space-y-2 w-full"
      data-testid="inline-material-picker"
      data-category={category}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="overline">Pick a {category.toLowerCase()} material</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => scroll(-1)}
            aria-label="Scroll left"
            className="p-1 rounded-sm hover:bg-[color:var(--ups-muted)]"
            data-testid="inline-picker-prev"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            onClick={() => scroll(+1)}
            aria-label="Scroll right"
            className="p-1 rounded-sm hover:bg-[color:var(--ups-muted)]"
            data-testid="inline-picker-next"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory"
        style={{ scrollbarWidth: "thin" }}
      >
        {data.items.map((m) => (
          <div
            key={m.id}
            className="snap-start shrink-0 flex flex-col rounded-sm overflow-hidden"
            style={{ width: 200, background: "#fff", border: "1px solid var(--ups-border)" }}
            data-testid={`inline-picker-card-${m.slug}`}
          >
            <button
              type="button"
              onClick={() => onOpenFull?.(m)}
              className="block aspect-square w-full text-left relative"
              style={{ background: "var(--ups-muted)" }}
              data-testid={`inline-picker-img-${m.slug}`}
            >
              <MaterialCarousel
                images={m.images && m.images.length > 0 ? m.images : (m.image_url ? [{ url: m.image_url, caption: null }] : [])}
                intervalMs={5000}
                showDots={m.images && m.images.length > 1}
                showCaption={false}
                className="w-full h-full"
                imgClassName="w-full h-full object-cover"
                ariaLabel={m.label}
                pauseOnHover
              />
              {m.tagline && (
                <span
                  className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] rounded-sm"
                  style={{
                    background: /popular|best/i.test(m.tagline) ? "#FFB500" : "#1C1917",
                    color: /popular|best/i.test(m.tagline) ? "#1C1917" : "#FFB500",
                  }}
                >
                  {m.tagline}
                </span>
              )}
            </button>
            <div className="p-2.5 flex-1 flex flex-col gap-1 min-w-0">
              <div className="font-display font-black text-[13px] leading-tight truncate" title={m.label}>{m.label}</div>
              {m.feels_like && (
                <div className="text-[11px] text-[color:var(--ups-subtext)] leading-snug line-clamp-2">
                  {m.feels_like}
                </div>
              )}
              <button
                type="button"
                onClick={() => onPick?.(m)}
                className="btn-brutal mt-1 px-2.5 py-1.5 text-[10px] rounded-sm font-bold uppercase tracking-[0.12em] flex items-center justify-center gap-1.5"
                data-testid={`inline-picker-pick-${m.slug}`}
              >
                <MessageSquare size={11} /> Pick this
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InlineMaterialPicker;
